/**
 * 42. Returns / RMAs — return request; approval; receipt;
 * replacement/credit.
 *
 * Arabic and English (C7): every word from the catalogs; statuses agree with
 * the return.
 */
import {
  Card,
  PageHeader,
  DataTable,
  Skeleton,
  StatusBadge,
} from "@/components/ui";
import { PendingButton } from "@/components/PendingAction";
import { useApiList } from "@/lib/api/hooks";
import { useTranslation } from "@/lib/i18n";
import { listTitle } from "@/lib/i18n/format";
import { formatMoney, formatDate } from "@/lib/money";

interface ReturnRow {
  id: string;
  returnNumber: string;
  returnDate: string;
  customerName: string;
  sourceInvoiceNumber: string;
  status:
    | "Requested"
    | "Approved"
    | "Received"
    | "Rejected"
    | "Credited"
    | "Replaced";
  refundAmount: string;
  currencyCode: string;
}

export function ReturnsPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useApiList<ReturnRow>(
    ["sales", "returns"],
    "/sales/returns",
  );

  return (
    <div>
      <PageHeader
        title={t("sales.returns.title")}
        subtitle={t("sales.returns.subtitle")}
        actions={
          <PendingButton variant="primary" reason="returns" slice="F5-W">
            {t("sales.returns.new")}
          </PendingButton>
        }
      />
      {isLoading || !data ? (
        <Skeleton rows={10} />
      ) : (
        <Card title={listTitle(t("sales.returns.list"), data.total)}>
          <DataTable<ReturnRow>
            rows={data.items}
            columns={[
              {
                key: "returnNumber",
                label: t("fields.number"),
                render: (r) => (
                  <span className="atlas-mono">{r.returnNumber}</span>
                ),
              },
              {
                key: "returnDate",
                label: t("fields.date"),
                render: (r) => formatDate(r.returnDate),
              },
              { key: "customerName", label: t("fields.customer") },
              {
                key: "sourceInvoiceNumber",
                label: t("sales.returns.sourceInvoice"),
                render: (r) => (
                  <span className="atlas-mono">{r.sourceInvoiceNumber}</span>
                ),
              },
              {
                key: "refundAmount",
                label: t("sales.returns.refund"),
                numeric: true,
                render: (r) => formatMoney(r.refundAmount, r.currencyCode),
              },
              {
                key: "status",
                label: t("fields.status"),
                render: (r) => (
                  <StatusBadge status={r.status} type="SalesReturn" />
                ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
